/** Step 2 — the admin account, and the rest of the team.
 *
 *  The admin is created with the tenant. The team list is only *recorded*
 *  (`config.onboarding.team_members`); the accounts are created later by the
 *  TEAM_USERS preflight item, which is why nothing here asks for a password.
 *  Collecting it at creation is the point: `team_members` is the one genuinely
 *  required preflight input that the create flow used to leave behind, so ops
 *  were asked for it a second time, on another screen, after the tenant existed.
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { isTeamEmail } from './teamMembers';
import type { TeamMemberInput, WizardData } from './types';

interface Props {
  data: WizardData;
  onChange: (partial: Partial<WizardData>) => void;
}

const BLANK: TeamMemberInput = { email: '', first_name: '', last_name: '', phone: '' };

export function Step2AdminUser({ data, onChange }: Props) {
  const [showPwd, setShowPwd] = useState(false);
  const members = data.team_members;

  const setMember = (i: number, patch: Partial<TeamMemberInput>) =>
    onChange({
      team_members: members.map((m, j) => (i === j ? { ...m, ...patch } : m)),
    });
  const addMember = () => onChange({ team_members: [...members, { ...BLANK }] });
  const removeMember = (i: number) =>
    onChange({ team_members: members.filter((_, j) => j !== i) });

  // The backend rejects the whole payload on a duplicate, so say so here.
  const duplicate = (i: number) => {
    const email = (members[i]?.email ?? '').trim().toLowerCase();
    if (!email) return false;
    return members.some((m, j) => j < i && m.email.trim().toLowerCase() === email);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          label="Email *"
          fullWidth
          size="small"
          value={data.admin_email}
          onChange={(e) => onChange({ admin_email: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Password *"
          fullWidth
          size="small"
          type={showPwd ? 'text' : 'password'}
          value={data.admin_password}
          onChange={(e) => onChange({ admin_password: e.target.value })}
          helperText="Minimum 8 characters."
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPwd((v) => !v)} edge="end">
                  {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="First name *"
          fullWidth
          size="small"
          value={data.admin_first_name}
          onChange={(e) => onChange({ admin_first_name: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Last name *"
          fullWidth
          size="small"
          value={data.admin_last_name}
          onChange={(e) => onChange({ admin_last_name: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          label="Phone"
          fullWidth
          size="small"
          value={data.admin_phone}
          onChange={(e) => onChange({ admin_phone: e.target.value })}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ mt: 1, mb: 2 }} />
        <Typography variant="subtitle2">The rest of the team</Typography>
        <Typography variant="caption" color="text.secondary">
          Optional. Recorded now, created later — the “Set up team members” checklist item makes
          the accounts and emails each person a set-password invite, so no passwords here. You can
          add or change them any time before that runs.
        </Typography>
      </Grid>

      {members.map((m, i) => (
        <Grid item xs={12} key={i}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Email *"
              size="small"
              sx={{ flex: '2 1 220px', minWidth: 0 }}
              value={m.email}
              error={Boolean(m.email) && (!isTeamEmail(m.email) || duplicate(i))}
              helperText={
                duplicate(i)
                  ? 'Already listed above'
                  : m.email && !isTeamEmail(m.email)
                    ? 'Not a valid email address'
                    : undefined
              }
              onChange={(e) => setMember(i, { email: e.target.value })}
            />
            <TextField
              label="First name"
              size="small"
              sx={{ flex: '1 1 140px', minWidth: 0 }}
              value={m.first_name}
              onChange={(e) => setMember(i, { first_name: e.target.value })}
            />
            <TextField
              label="Last name"
              size="small"
              sx={{ flex: '1 1 140px', minWidth: 0 }}
              value={m.last_name}
              onChange={(e) => setMember(i, { last_name: e.target.value })}
            />
            <TextField
              label="Phone"
              size="small"
              sx={{ flex: '1 1 130px', minWidth: 0 }}
              value={m.phone}
              onChange={(e) => setMember(i, { phone: e.target.value })}
            />
            <Tooltip title="Remove">
              <IconButton
                size="small"
                sx={{ mt: 0.5 }}
                aria-label={`Remove team member ${i + 1}`}
                onClick={() => removeMember(i)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button size="small" startIcon={<AddIcon />} onClick={addMember}>
          Add team member
        </Button>
      </Grid>
    </Grid>
  );
}
